import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import AppLogo from "@/assets/images/favicon.png";

import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { Loader } from "@/components/Loader/Loader";

import { AddOns } from "./AddOns";
import { BillingToggle } from "./BillingToggle";
import { PlanCard } from "./PlanCard";
import { PlanSelectorProps } from "./types";
import { UniversalBenefits } from "./UniversalBenefits";

/**
 * Shared presentational checkout layout used by both the onboarding and the
 * billing-triggered plan-selection pages. Holds no data/redux logic — the page
 * wrappers own the Stripe hooks and pass everything in via props.
 */
export const PlanSelector: FunctionComponent<PlanSelectorProps> = ({
	heading,
	plans,
	isLoading,
	selectedProductId,
	onSelect,
	availableIntervals,
	activeInterval,
	onIntervalChange,
	primaryCtaLabel,
	onPrimaryCta,
	isCtaLoading,
	ctaDisabled,
	onCancel,
	cancelLabel,
	errorMessage,
	footerNote,
	footerSlot,
}) => {
	const { t } = useTranslation();
	const isAnnual = activeInterval !== "month";

	return (
		<div className="w-full flex flex-col items-center">
			<div className="flex flex-col items-center bg-white px-6 py-10 rounded-[20px] w-full max-w-[920px] shadow-[0px_0px_80px_0px_rgba(171,178,217,0.35)] mb-6">
				<img alt="logo" className="w-12 h-12 mb-4" src={AppLogo} />
				{heading}
				<p className="text-center text-sm text-text-meta max-w-[520px] mb-7">
					{t("usage.subscription.checkout.subheading")}
				</p>

				<UniversalBenefits />

				<BillingToggle
					activeInterval={activeInterval}
					availableIntervals={availableIntervals}
					onChange={onIntervalChange}
				/>

				{isLoading ? (
					<div className="my-10">
						<Loader />
					</div>
				) : (
					<div className="grid grid-cols-1 md:grid-cols-3 gap-4 w-full mt-7">
						{plans.map((plan) => (
							<PlanCard
								key={plan.productId}
								interval={activeInterval}
								plan={plan}
								selected={plan.productId === selectedProductId}
								onSelect={() => onSelect(plan.productId)}
							/>
						))}
					</div>
				)}

				<div className="w-full mt-6">
					<AddOns mode={isAnnual ? "annual" : "monthly"} />
				</div>

				{errorMessage && (
					<div className="rounded-lg bg-status-error-light border border-status-error p-3 text-sm text-status-error text-center mt-6 w-full max-w-[400px]">
						{errorMessage}
					</div>
				)}

				<div className="flex flex-row gap-4 mt-7">
					{onCancel && (
						<Button
							color={ButtonColor.ACTION_SECONDARY}
							size={ButtonSize.L}
							title={cancelLabel || ""}
							onClick={onCancel}
						/>
					)}
					<Button
						color={ButtonColor.ACTION}
						disabled={ctaDisabled}
						isLoading={isCtaLoading}
						size={ButtonSize.L}
						title={primaryCtaLabel}
						onClick={onPrimaryCta}
					/>
				</div>

				<div className="text-center text-xs text-text-meta mt-4 leading-relaxed">{footerNote}</div>
			</div>

			{footerSlot}
		</div>
	);
};
